import { useEffect, useRef, useState } from "react";
import { isExpired } from "../../utils/deliveryToken";
import { useTranslation } from "../../i18n";

function formatRemaining(ms, t) {
    if (ms <= 0) {
        return null;
    }

    const totalSeconds = Math.floor(ms / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    const parts = [];
    if (days > 0) {
        parts.push(`${days} ${t("delivery.countdown.day")}`);
    }
    if (hours > 0 || days > 0) {
        parts.push(`${hours} ${t("delivery.countdown.hour")}`);
    }
    if (minutes > 0 || hours > 0 || days > 0) {
        parts.push(`${minutes} ${t("delivery.countdown.minute")}`);
    }
    if (days === 0 && hours === 0) {
        parts.push(`${seconds} ${t("delivery.countdown.second")}`);
    }

    return parts.join(" ");
}

function ExpiryCountdown({ expiryUtcSeconds, onExpired, compact = false, className = "" }) {
    const { t } = useTranslation();
    const onExpiredRef = useRef(onExpired);
    const expiredNotifiedRef = useRef(false);
    const [remaining, setRemaining] = useState(() => expiryUtcSeconds * 1000 - Date.now());

    useEffect(() => {
        onExpiredRef.current = onExpired;
    }, [onExpired]);

    useEffect(() => {
        expiredNotifiedRef.current = false;
        setRemaining(expiryUtcSeconds * 1000 - Date.now());

        const notifyExpired = () => {
            if (expiredNotifiedRef.current) {
                return;
            }
            expiredNotifiedRef.current = true;
            onExpiredRef.current?.();
        };

        if (isExpired(expiryUtcSeconds)) {
            notifyExpired();
            return undefined;
        }

        const tick = () => {
            const ms = expiryUtcSeconds * 1000 - Date.now();
            setRemaining(ms);
            if (ms <= 0) {
                notifyExpired();
            }
        };

        tick();
        const id = setInterval(tick, 1000);

        return () => clearInterval(id);
    }, [expiryUtcSeconds]);

    const formatted = formatRemaining(remaining, t);

    if (!formatted) {
        return null;
    }

    return (
        <div
            className={`delivery-countdown ${compact ? "delivery-countdown--compact" : ""} ${className}`.trim()}
            role="timer"
            aria-live="polite"
        >
            <span className="delivery-countdown__label">{t("delivery.countdown.remaining")}</span>
            <span className="delivery-countdown__value">{formatted}</span>
        </div>
    );
}

export default ExpiryCountdown;
